import { useEffect, useState, useCallback } from "react";
import {
  Users, Bot, Globe, Clock, Activity, Download, LogOut, RefreshCw, PlugZap, CheckCircle2,
  Copy, MapPin, Server, Link2, FileText, Tag, Smartphone, Search, Building2, Megaphone, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { getDashboard, getBots, getVisitors, getShopStatus, getInstallUrl, clearVisitorLogs, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AdminSettings } from "@/pages/admin/AdminSettings";
import { InventoryManager } from "@/pages/admin/InventoryManager";
import { TranslationManager } from "@/pages/admin/TranslationManager";
import { CMSManager } from "@/pages/admin/CMSManager";
import { BackordersManager } from "@/pages/admin/BackordersManager";
import { VisitorAudit } from "@/pages/admin/VisitorAudit";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { StatTile, TrendChart, BreakdownList, codeToFlag, fmtDuration } from "@/pages/admin/analyticsUi";

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const [days, setDays] = useState("30");
  const [includeBots, setIncludeBots] = useState(false);
  const [data, setData] = useState(null);
  const [bots, setBots] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);

  // Visitor-log filters (Intellix-style)
  const [fCountry, setFCountry] = useState("");
  const [fPath, setFPath] = useState("");
  const [fIp, setFIp] = useState("");
  const [pageSize, setPageSize] = useState("50");

  // Active tab — drives which controls/graphs are relevant.
  const [tab, setTab] = useState("audience");
  const ANALYTICS_TABS = ["audience", "acquisition", "content", "visitors", "audit"];
  const isAnalyticsTab = ANALYTICS_TABS.includes(tab);
  const showTraffic = ["audience", "acquisition", "content", "visitors"].includes(tab);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { days: Number(days), include_bots: includeBots };
      const [d, b, s] = await Promise.all([
        getDashboard(params), getBots({ days: Number(days) }), getShopStatus(),
      ]);
      setData(d); setBots(b); setShop(s);
    } catch {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [days, includeBots]);

  const loadVisitors = useCallback(async () => {
    try {
      const v = await getVisitors({
        days: Number(days), include_bots: includeBots, limit: Number(pageSize),
        country: fCountry || undefined, path_contains: fPath || undefined, ip_prefix: fIp || undefined,
      });
      setVisitors(v.visitors || []);
    } catch { /* keep previous */ }
  }, [days, includeBots, pageSize, fCountry, fPath, fIp]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(loadVisitors, 300); // debounce filter typing
    return () => clearTimeout(t);
  }, [loadVisitors]);

  const connect = async () => {
    try {
      const { install_url } = await getInstallUrl();
      window.open(install_url, "_blank", "noopener");
      toast.info("Shopify authorization opened", { description: "Approve the app, then refresh this page." });
    } catch {
      toast.error("Could not start connection");
    }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("kh_admin_token");
    const url = `${API}/admin/analytics/export?days=${days}&include_bots=true`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "analytics_export.csv";
        a.click();
      })
      .catch(() => toast.error("Export failed"));
  };

  const s = data?.summary;

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-border bg-card/80 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            <span className="font-display text-lg">Kentucky Hub</span>
            <Badge variant="secondary" className="ml-1">Analytics</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden text-sm text-muted-foreground sm:inline">{admin?.email}</span>
            <Button variant="ghost" size="icon" onClick={logout} aria-label="Logout" data-testid="admin-logout-button"><LogOut className="h-4 w-4" /></Button>
          </div>
        </div>
      </header>

      <main data-testid="admin-dashboard" className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6">
        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-display text-2xl">Traffic &amp; Insights</h1>
            <p className="text-sm text-muted-foreground">Who visited, from where, and what to improve.</p>
          </div>
          {isAnalyticsTab && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch id="bots" checked={includeBots} onCheckedChange={setIncludeBots} data-testid="admin-include-bots-toggle" />
                <Label htmlFor="bots" className="text-sm">Include bots</Label>
              </div>
              <Select value={days} onValueChange={setDays}>
                <SelectTrigger className="w-[130px]" data-testid="admin-date-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={load} aria-label="Refresh" data-testid="admin-refresh-button"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></Button>
              <Button variant="outline" onClick={exportCsv} data-testid="admin-export-button"><Download className="mr-2 h-4 w-4" /> Export</Button>
            </div>
          )}
        </div>

        {/* Tabs — navigation comes first, graphs only on relevant tabs */}
        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList className="flex flex-wrap">
            <TabsTrigger value="audience" data-testid="admin-tab-audience">Audience</TabsTrigger>
            <TabsTrigger value="acquisition" data-testid="admin-tab-acquisition">Acquisition</TabsTrigger>
            <TabsTrigger value="content" data-testid="admin-tab-content">Content</TabsTrigger>
            <TabsTrigger value="visitors" data-testid="admin-tab-visitors">Visitor Logs</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">Visitor Audit</TabsTrigger>
            <TabsTrigger value="inventory" data-testid="admin-tab-inventory">Inventory</TabsTrigger>
            <TabsTrigger value="images" data-testid="admin-tab-images">Images</TabsTrigger>
            <TabsTrigger value="backorders" data-testid="admin-tab-backorders">Backorders</TabsTrigger>
            <TabsTrigger value="translations" data-testid="admin-tab-translations">Translations</TabsTrigger>
            <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
          </TabsList>

          {/* Traffic overview — only on analytics tabs, kept compact */}
          {showTraffic && (
            <div className="mt-5 space-y-5">
              {/* Shopify connection */}
              <div className="rounded-xl border border-border bg-card p-5" data-testid="admin-shopify-connect">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    {shop?.connected ? <CheckCircle2 className="h-6 w-6 text-chart-2" /> : <PlugZap className="h-6 w-6 text-primary" />}
                    <div>
                      <p className="font-medium">Shopify Store {shop?.connected ? "Connected" : "Not Connected"}</p>
                      <p className="text-xs text-muted-foreground">{shop?.store}{shop?.installed_at ? ` \u00b7 connected ${new Date(shop.installed_at).toLocaleDateString()}` : ""}</p>
                    </div>
                  </div>
                  {!shop?.connected && <Button onClick={connect} data-testid="admin-connect-shopify-button"><PlugZap className="mr-2 h-4 w-4" /> Connect Store</Button>}
                </div>
                {!shop?.connected && shop?.redirect_uri && (
                  <div className="mt-4 rounded-lg border border-dashed border-border bg-background/50 p-3">
                    <p className="text-xs text-muted-foreground">First, add this URL to your Shopify app's <strong>Allowed redirection URL(s)</strong>:</p>
                    <div className="mt-1.5 flex items-center gap-2">
                      <code className="flex-1 truncate rounded bg-secondary px-2 py-1 font-mono text-xs">{shop.redirect_uri}</code>
                      <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard.writeText(shop.redirect_uri); toast.success("Copied"); }} aria-label="Copy"><Copy className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                )}
              </div>

              {/* KPIs */}
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
                <StatTile icon={Users} label="Sessions" value={fmtNumSafe(s?.sessions)} sub={`${fmtNumSafe(s?.total_events)} events`} tint="blue" testid="admin-kpi-card" />
                <StatTile icon={Activity} label="Human events" value={fmtNumSafe(s?.human_events)} tint="green" testid="admin-kpi-card" />
                <StatTile icon={Bot} label="Bot events" value={fmtNumSafe(s?.bot_events)} tint="amber" testid="admin-kpi-card" />
                <StatTile icon={Globe} label="Countries" value={fmtNumSafe(s?.countries)} tint="violet" testid="admin-kpi-card" />
                <StatTile icon={Clock} label="Avg time on page" value={`${s?.avg_time_on_page_s ?? 0}s`} tint="slate" testid="admin-kpi-card" />
              </div>

              {/* Compact trend */}
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Daily trend</h3>
                  <span className="text-xs text-muted-foreground">Sessions vs events</span>
                </div>
                <div className="mt-3">
                  <TrendChart data={data?.timeseries} />
                </div>
              </div>
            </div>
          )}


          <TabsContent value="audience" className="mt-4 grid gap-4 lg:grid-cols-2">
            <BreakdownList title="Top countries" icon={Globe} rows={data?.top_countries} showFlag />
            <BreakdownList title="Top cities" icon={MapPin} rows={data?.top_cities} showFlag />
            <BreakdownList title="Top ISPs / networks" icon={Server} rows={data?.isps || []} />
            <BreakdownList title="Devices" icon={Smartphone} rows={data?.devices} />
          </TabsContent>

          <TabsContent value="acquisition" className="mt-4 grid gap-4 lg:grid-cols-2">
            <BreakdownList title="Traffic sources" icon={Megaphone} rows={data?.sources} />
            <BreakdownList title="Channels" icon={Link2} rows={data?.channels} />
            <BreakdownList title="Referring sites" icon={Link2} rows={data?.referrers} />
            <BreakdownList title="Search engines (organic)" icon={Search} rows={data?.search_engines} />
            <BreakdownList title="Search keywords (when provided)" icon={Search} rows={data?.keywords} />
            <BreakdownList title="Campaigns (UTM)" icon={Megaphone} rows={data?.campaigns} />
          </TabsContent>

          <TabsContent value="content" className="mt-4 grid gap-4 lg:grid-cols-2">
            <BreakdownList title="Top pages" icon={FileText} rows={data?.top_pages} />
            <BreakdownList title="Top products" icon={Tag} rows={data?.top_products} />
            <BreakdownList title="By brand" icon={Building2} rows={data?.brands} />
            <BreakdownList title="Crawlers / bots detected" icon={Bot} rows={bots?.bots} />
          </TabsContent>

          <TabsContent value="visitors" className="mt-4">
            <VisitorLogs
              visitors={visitors}
              fCountry={fCountry} setFCountry={setFCountry}
              fPath={fPath} setFPath={setFPath}
              fIp={fIp} setFIp={setFIp}
              pageSize={pageSize} setPageSize={setPageSize}
              onRefresh={loadVisitors}
              onCleared={() => { load(); loadVisitors(); }}
            />
          </TabsContent>

          <TabsContent value="audit" className="mt-4">
            <VisitorAudit days={Number(days)} includeBots={includeBots} />
          </TabsContent>

          <TabsContent value="inventory" className="mt-4">
            <InventoryManager />
          </TabsContent>

          <TabsContent value="images" className="mt-4">
            <CMSManager />
          </TabsContent>

          <TabsContent value="backorders" className="mt-4">
            <BackordersManager />
          </TabsContent>

          <TabsContent value="translations" className="mt-4">
            <TranslationManager />
          </TabsContent>

          <TabsContent value="settings" className="mt-4">
            <AdminSettings />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const fmtNumSafe = (n) => (n == null ? "\u2014" : new Intl.NumberFormat("en").format(n));

// ---- Intellix-style Visitor Logs (filters + detailed table) ----
const VisitorLogs = ({ visitors, fCountry, setFCountry, fPath, setFPath, fIp, setFIp, pageSize, setPageSize, onRefresh, onCleared }) => {
  const [clearing, setClearing] = useState(false);
  const doClear = async () => {
    setClearing(true);
    try {
      const res = await clearVisitorLogs(0);
      toast.success("Visitor logs cleared", { description: `${res.events_deleted} events \u00b7 ${res.sessions_deleted} sessions removed.` });
      onCleared?.();
    } catch {
      toast.error("Could not clear logs");
    } finally { setClearing(false); }
  };

  return (
  <div className="space-y-4" data-testid="admin-visitors-table">
    {/* Toolbar */}
    <div className="flex items-center justify-between gap-3">
      <p className="text-sm text-muted-foreground">Raw visit log with geo, referrer and device detail.</p>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" data-testid="clear-logs-button"><Trash2 className="mr-1.5 h-3.5 w-3.5" /> Clear logs</Button>
        </AlertDialogTrigger>
        <AlertDialogContent data-testid="clear-logs-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all visitor logs?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes all stored visitor events and sessions (including old development traffic).
              Shopify orders are kept. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doClear} disabled={clearing} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="clear-logs-confirm">{clearing ? "Clearing\u2026" : "Yes, clear logs"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>

    {/* Filters */}
    <div className="grid gap-3 rounded-xl border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-4">
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Country (ISO-2)</Label>
        <Input value={fCountry} onChange={(e) => setFCountry(e.target.value.toUpperCase().slice(0, 2))} placeholder="e.g. IE, US" data-testid="filter-country" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Path contains</Label>
        <Input value={fPath} onChange={(e) => setFPath(e.target.value)} placeholder="/product/..." data-testid="filter-path" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">IP starts with</Label>
        <Input value={fIp} onChange={(e) => setFIp(e.target.value)} placeholder="e.g. 84.236" data-testid="filter-ip" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Page size</Label>
        <div className="flex gap-2">
          <Select value={pageSize} onValueChange={setPageSize}>
            <SelectTrigger data-testid="filter-pagesize"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="25">25 rows</SelectItem>
              <SelectItem value="50">50 rows</SelectItem>
              <SelectItem value="100">100 rows</SelectItem>
              <SelectItem value="200">200 rows</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={onRefresh} aria-label="Refresh logs" data-testid="visitors-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>

    {/* Logs table */}
    <div className="rounded-xl border border-border bg-card">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Path</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>Referrer</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>City</TableHead>
              <TableHead>ISP</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Dwell</TableHead>
              <TableHead>Agent</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visitors.length ? visitors.map((v, i) => (
              <TableRow key={i} data-testid="visitor-log-row">
                <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{v.created_at ? new Date(v.created_at).toLocaleString() : "\u2014"}</TableCell>
                <TableCell className="max-w-[160px] truncate font-mono text-xs" title={v.path}>{v.path}</TableCell>
                <TableCell className="whitespace-nowrap font-mono text-xs">{v.ip || "\u2014"}</TableCell>
                <TableCell className="max-w-[120px] truncate text-xs text-muted-foreground" title={v.referrer || v.referrer_host}>{v.referrer_host || (v.channel === "direct" ? "Direct" : "\u2014")}</TableCell>
                <TableCell className="whitespace-nowrap text-xs"><span className="mr-1">{codeToFlag(v.country_code)}</span>{v.country_code || "\u2014"}</TableCell>
                <TableCell className="max-w-[110px] truncate text-xs" title={v.city}>{v.city || "\u2014"}</TableCell>
                <TableCell className="max-w-[130px] truncate text-xs text-muted-foreground" title={v.isp}>{v.isp || "\u2014"}</TableCell>
                <TableCell>{v.is_bot ? <Badge variant="secondary">bot</Badge> : <Badge className="bg-chart-2 text-white hover:bg-chart-2">human</Badge>}</TableCell>
                <TableCell className="whitespace-nowrap text-right font-mono text-xs">{fmtDuration(v.time_spent_ms)}</TableCell>
                <TableCell className="max-w-[160px] truncate text-xs text-muted-foreground" title={v.user_agent}>{v.user_agent || "\u2014"}</TableCell>
              </TableRow>
            )) : (
              <TableRow><TableCell colSpan={10} className="py-10 text-center text-sm text-muted-foreground">No visitors match these filters</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  </div>
  );
};
